import { MapContainer, TileLayer, Marker, useMap, Popup } from "react-leaflet";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaMapMarkerAlt, FaSearch, FaCrosshairs, FaInfoCircle, FaUserCheck, FaSave, FaLocationArrow, FaEdit, FaCheck,FaFileAlt, } from "react-icons/fa";
import UserDashboard from './UserDashboard';
import workerAPI from "../api/workerAPI";
import marketingAPI from "../api/marketingAPI";
import { useMapEvents } from "react-leaflet";


// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for selected location
const selectedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map when location changes
const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e);
    },
  });
  return null;
};


const AdminMarketingMap = () => {
  // Map and location states
  const [mapCenter, setMapCenter] = useState([11.1271, 78.6569]);
  const [location, setLocation] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(13);
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // NEW STATES for marketing team functionality
  const [marketingTeam, setMarketingTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Show alert message
  const showAlert = (message, type = "info") => {
    switch(type) {
      case "success":
        alert("✅ SUCCESS\n" + message);
        break;
      case "error":
        alert("❌ ERROR\n" + message);
        break;
      case "warning":
        alert("⚠️ WARNING\n" + message);
        break;
      default:
        alert("ℹ️ INFO\n" + message);
    }
  };

  // Show confirmation dialog
  const showConfirmation = (message) => {
    return window.confirm("❓ CONFIRM\n" + message);
  };

  // NEW: Function to fetch marketing team members
  const fetchMarketingTeam = async () => {
    setLoadingTeam(true);
    try {
      const token = localStorage.getItem("token");
      const response = await workerAPI.get('/team/marketing', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMarketingTeam(response.data.team);
        
        if (response.data.count === 0) {
          showAlert("No marketing team members found in your company", "warning");
        }
      } else {
        showAlert("Failed to load team members", "error");
      }
    } catch (error) {
      console.error("Error fetching marketing team:", error);
      showAlert("Could not load team members. Please try again.", "error");
    } finally {
      setLoadingTeam(false);
    }
  };

  // Fetch marketing team on component mount and when refreshTrigger changes
  useEffect(() => {
    fetchMarketingTeam();
  }, [refreshTrigger]);

  // Handle search input change with debouncing for suggestions
  const handleSearchChange = (value) => {
    setSearch(value);
    setManualAddress(value);
    
    if (isManualMode) {
      // In manual mode, just update the search text
      setShowSuggestions(false);
      return;
    }
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If input is cleared, clear results
    if (!value.trim()) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }
    
    // Set timeout for debounced suggestions
    searchTimeoutRef.current = setTimeout(() => {
      if (value.trim().length >= 3) {
        fetchAddressSuggestions(value);
      }
    }, 300);
  };

  // Fetch address suggestions
  const fetchAddressSuggestions = async (query) => {
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await res.json();
      setSearchResults(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Search error:", error);
      showAlert("Failed to search for address", "error");
    } finally {
      setIsSearching(false);
    }
  };

  // Select address from suggestions
  const selectSuggestion = (item) => {
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lon);

    const newLocation = {
      latitude: lat,
      longitude: lng,
      address: item.display_name,
    };

    setLocation(newLocation);
    setMapCenter([lat, lng]);
    setSearch(item.display_name);
    setManualAddress(item.display_name);
    setSearchResults([]);
    setShowSuggestions(false);
    setZoomLevel(16);
    
    showAlert(`Location selected: ${item.display_name.split(",")[0]}`, "success");
  };

  // Handle manual address confirmation
  const confirmManualAddress = () => {
    if (!manualAddress.trim()) {
      showAlert("Please enter an address", "error");
      return;
    }

    // Ask for confirmation in manual mode
    if (!showConfirmation(`Use this manual address?\n"${manualAddress}"`)) {
      return;
    }

    // For manual address, we'll geocode it
    setIsLoading(true);
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(manualAddress)}&limit=1`
    )
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          const item = data[0];
          const lat = parseFloat(item.lat);
          const lng = parseFloat(item.lon);

          const newLocation = {
            latitude: lat,
            longitude: lng,
            address: manualAddress,
          };

          setLocation(newLocation);
          setMapCenter([lat, lng]);
          setSearch(manualAddress);
          setZoomLevel(16);
          setShowSuggestions(false);
          
          showAlert(`Location found for: ${manualAddress}`, "success");
        } else {
          // If no geocode result, still allow manual address
          const newLocation = {
            latitude: mapCenter[0],
            longitude: mapCenter[1],
            address: manualAddress,
          };
          
          setLocation(newLocation);
          showAlert(`Manual address saved. You may need to adjust the map marker.`, "warning");
        }
      })
      .catch(error => {
        console.error("Geocoding error:", error);
        // Still allow manual address entry
        const newLocation = {
          latitude: mapCenter[0],
          longitude: mapCenter[1],
          address: manualAddress,
        };
        
        setLocation(newLocation);
        showAlert(`Manual address saved (geocoding failed).`, "warning");
      })
      .finally(() => {
        setIsLoading(false);
        setIsManualMode(false);
      });
  };

  // Toggle manual mode
  const toggleManualMode = () => {
    if (!isManualMode) {
      // Entering manual mode - ask for confirmation
      if (!showConfirmation("Switch to Manual Address Mode?\n\nIn this mode, you can type any address without auto-suggestions.")) {
        return;
      }
      setIsManualMode(true);
      setManualAddress(search);
      setShowSuggestions(false);
      showAlert("Manual address mode enabled. Type your address and click Confirm.", "info");
    } else {
      // Exiting manual mode
      setIsManualMode(false);
      showAlert("Auto-suggest mode enabled.", "info");
    }
  };

  // Handle map click
  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;
    
    // Ask for confirmation
    if (!showConfirmation(`Select this location?\n\nLatitude: ${lat.toFixed(6)}\nLongitude: ${lng.toFixed(6)}`)) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      
      const newLocation = {
        latitude: lat,
        longitude: lng,
        address: data.display_name || `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      };
      
      setLocation(newLocation);
      setSearch(data.display_name || `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      setManualAddress(data.display_name || `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      setMapCenter([lat, lng]);
      setZoomLevel(16);
      setShowSuggestions(false);
      
      showAlert("Location selected from map", "success");
    } catch (error) {
      console.error("Error fetching address:", error);
      const newLocation = {
        latitude: lat,
        longitude: lng,
        address: `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      };
      setLocation(newLocation);
      setSearch(`Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      setManualAddress(`Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      showAlert("Selected location (could not fetch address details)", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  // Use current location
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      showAlert("Geolocation is not supported by your browser", "error");
      return;
    }
    
    // Ask for permission
    if (!showConfirmation("Allow this site to access your current location?")) {
      return;
    }
    
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();
          
          const newLocation = {
            latitude,
            longitude,
            address: data.display_name || "Current Location",
          };
          
          setLocation(newLocation);
          setSearch(data.display_name || "Current Location");
          setManualAddress(data.display_name || "Current Location");
          setMapCenter([latitude, longitude]);
          setZoomLevel(16);
          setShowSuggestions(false);
          
          showAlert("Current location found", "success");
        } catch (error) {
          console.error("Error fetching address:", error);
          const newLocation = {
            latitude,
            longitude,
            address: `Current Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
          };
          setLocation(newLocation);
          setSearch(`Current Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          setManualAddress(`Current Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          showAlert("Current location found (could not get address)", "warning");
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        showAlert("Unable to retrieve your location. Please check location permissions.", "error");
        setIsLoading(false);
      }
    );
  };

  const submitLocation = async () => {
    if (!location) {
      showAlert("Please select a location first", "error");
      return;
    }
    
    if (!description.trim()) {
      showAlert("Please add a description", "error");
      return;
    }
    
    if (!assignedTo) {
      showAlert("Please assign this location to a team member", "error");
      return;
    }

    const selectedMember = marketingTeam.find(member => member.id === assignedTo);
    if (!selectedMember) {
      showAlert("Invalid team member selected", "error");
      return;
    }

    // Show final confirmation before submitting
    const confirmationMessage = `
Assign this location?

📍 Location: ${location.address.split(",")[0]}
👤 Assign To: ${selectedMember.name}
📝 Description: ${description}

Do you want to proceed?`;

    if (!showConfirmation(confirmationMessage)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      
      // Get user info
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
     let addedById, addedByName, company;

if (userData && userData._id) {

  addedById = userData._id;
  company = userData.company || userData.companyId;

  addedByName =
    userData.name ||
    userData.username ||
    userData.fullName ||
    userData.email?.split("@")[0] ||
    "User";

} else {
  try {
    const tokenData = JSON.parse(atob(token.split('.')[1]));

    addedById = tokenData.id || tokenData._id;
    company = tokenData.company;

    addedByName =
      tokenData.name ||
      tokenData.username ||
      tokenData.fullName ||
      tokenData.email?.split("@")[0] ||
      "User";

  } catch (decodeError) {
    console.error("Failed to decode token:", decodeError);
    throw new Error("Authentication failed. Please login again.");
  }
}
      
     const payload = {
  latitude: parseFloat(location.latitude),
  longitude: parseFloat(location.longitude),
  address: location.address,
  description: description,
  assignedTo: assignedTo,
  assignedToName: selectedMember.name || "",
  assignedToEmail: selectedMember.email || "",
  company: company,
  addedBy: addedById,
  addedByName: addedByName,
  status: "pending"
};
      
      console.log("Submitting payload:", payload);

      const response = await marketingAPI.post('/add', payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      if (response.data.success) {
        showAlert(`✅ SUCCESS!\n\nLocation successfully assigned to ${selectedMember.name}!`, "success");
        
        // Reset form
        setDescription("");
        setAssignedTo("");
        setLocation(null);
        setSearch("");
        setManualAddress("");
        
        setRefreshTrigger(prev => prev + 1);
      } else {
        throw new Error(response.data.message || 'Failed to assign location');
      }
      
    } catch (error) {
      console.error("Submission error:", error);
      
      let errorMessage = "Failed to assign location.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showAlert(`❌ SUBMISSION FAILED\n\n${errorMessage}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear location with confirmation
  const clearLocation = () => {
    if (!location) return;
    
    if (showConfirmation("Are you sure you want to clear the selected location?")) {
      setLocation(null);
      setSearch("");
      setManualAddress("");
      setSearchResults([]);
      setShowSuggestions(false);
      setZoomLevel(13);
      setIsManualMode(false);
      showAlert("Location cleared", "info");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
         <UserDashboard />
      {/* Header */}
<div className="flex justify-between items-center mb-5 mt-20">
  <div>
    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
      <FaMapMarkerAlt className="text-blue-600" />
      Marketing Location Manager
    </h1>
    {location && (
      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
        <FaMapMarkerAlt size={12} />
        <span>Location Selected</span>
      </div>
    )}
  </div>
  
  {/* Add this button */}
  <button
    onClick={() => window.location.href = '/marketing-members'}
    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
  >
    <FaFileAlt />
    View Reports
  </button>
</div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT – Search & Map */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search Card */}
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <FaSearch className="text-blue-500" />
                Address Search
              </h2>
              <button
                onClick={toggleManualMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isManualMode ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <FaEdit size={14} />
                {isManualMode ? "Auto Mode" : "Manual Mode"}
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="relative">
                <div className="flex gap-2">
                  <input
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={isManualMode ? "Type address manually..." : "Start typing for suggestions..."}
                    className="flex-grow input border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl px-4 py-3 transition-all"
                  />
                  
                  {isManualMode ? (
                    <button
                      onClick={confirmManualAddress}
                      disabled={!manualAddress.trim() || isLoading}
                      className="bg-green-600 hover:bg-green-700 text-white font-medium px-5 py-3 rounded-xl flex items-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <FaCheck />
                      Confirm Address
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (!search.trim()) {
                          showAlert("Please enter search terms", "error");
                          return;
                        }
                        fetchAddressSuggestions(search);
                      }}
                      disabled={!search.trim() || isSearching}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-xl flex items-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSearching ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Searching...
                        </>
                      ) : (
                        <>
                          <FaSearch />
                          Search
                        </>
                      )}
                    </button>
                  )}
                </div>

                {!isManualMode && showSuggestions && searchResults.length > 0 && (
                  <div className="absolute z-20 bg-white border border-gray-200 w-full mt-2 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-3 border-b border-gray-100 bg-gray-50 font-medium text-gray-700">
                      Suggestions ({searchResults.length})
                    </div>
                    {searchResults.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => selectSuggestion(item)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 text-blue-600 rounded-full p-2 mt-1">
                            <FaMapMarkerAlt size={14} />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-800">{item.display_name.split(",")[0]}</div>
                            <div className="text-sm text-gray-500 line-clamp-2">
                              {item.display_name.split(",").slice(1).join(",")}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 flex gap-4">
                              <span>Lat: {parseFloat(item.lat).toFixed(6)}</span>
                              <span>Lon: {parseFloat(item.lon).toFixed(6)}</span>
                            </div>
                          </div>
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                            Select
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {isManualMode && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-700 text-sm">
                      <FaInfoCircle />
                      <span>Manual Mode: Type any address and click "Confirm Address"</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={useCurrentLocation}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                >
                  <FaCrosshairs />
                  Use My Location
                </button>
                
                {location && (
                  <button
                    onClick={clearLocation}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                  >
                    ✕ Clear Location
                  </button>
                )}
              </div>
            </div>
            
            <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
              <FaInfoCircle />
              {isManualMode 
                ? "Tip: Type any address and confirm. The system will try to find it on the map."
                : "Tip: Type at least 3 characters for auto-suggestions, or click Search button"
              }
            </div>
          </div>

          {/* Map Card */}
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Location Map</h2>
              {location && (
                <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg">
                  <FaLocationArrow />
                  <span>Location Selected</span>
                </div>
              )}
            </div>
            
            <div className="relative">
              {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-xl">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-3 text-gray-600">Loading location...</p>
                  </div>
                </div>
              )}
              
              <MapContainer
  center={mapCenter}
  zoom={zoomLevel}
  className="h-[400px] md:h-[500px] rounded-xl z-0"
  scrollWheelZoom={true}
>
  <TileLayer
    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">
    OpenStreetMap</a> contributors'
    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
  />

  <RecenterMap center={mapCenter} zoom={zoomLevel} />

  {/* ✅ FIXED MAP CLICK HANDLER */}
  <MapClickHandler onMapClick={handleMapClick} />

  {location && (
    <Marker
      position={[location.latitude, location.longitude]}
      icon={selectedIcon}
    >
      <Popup>
        <div className="font-semibold text-gray-800">📍 Selected Location</div>
        <div className="text-sm mt-1">{location.address}</div>
        <div className="text-xs text-gray-500 mt-2">
          {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
        </div>
      </Popup>
    </Marker>
  )}
</MapContainer>

              
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg z-[1000]">
                <div className="text-sm font-medium text-gray-700">Click on map to select location</div>
                <div className="text-xs text-gray-500">Click anywhere to drop a pin</div>
              </div>
            </div>
            
            {location && (
              <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 text-blue-600 rounded-full p-2">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-blue-800">Selected Location</div>
                    <div className="text-sm text-blue-600 mt-1 truncate">{location.address}</div>
                    <div className="text-xs text-blue-500 mt-1">
                      Coordinates: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    </div>
                  </div>
                  {isManualMode && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                      Manual Address
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT – Details Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-lg p-5 sticky top-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <FaUserCheck className="text-green-500" />
              Assignment Details
            </h2>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordinates</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Latitude</div>
                    <input 
                      value={location?.latitude?.toFixed(6) || ""} 
                      readOnly 
                      className="w-full input bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 font-mono text-sm"
                      placeholder="Not selected"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Longitude</div>
                    <input 
                      value={location?.longitude?.toFixed(6) || ""} 
                      readOnly 
                      className="w-full input bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 font-mono text-sm"
                      placeholder="Not selected"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <div className="relative">
                  <textarea
                    value={location?.address || ""}
                    readOnly
                    rows={3}
                    className="w-full input bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700"
                    placeholder="Search or click on map to select location"
                  />
                  {isManualMode && location && (
                    <div className="absolute -top-2 right-2">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                        Manual
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full input border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg px-3 py-2.5 transition-all"
                  placeholder="Enter marketing activity details..."
                />
                <div className="text-xs text-gray-500 mt-1">What marketing activities will happen here?</div>
              </div>
              
              {/* UPDATED: Assign To Dropdown with Dynamic Marketing Team */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To <span className="text-red-500">*</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (showConfirmation("Refresh marketing team list?")) {
                        setRefreshTrigger(prev => prev + 1);
                      }
                    }}
                    disabled={loadingTeam}
                    className="ml-2 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded"
                  >
                    {loadingTeam ? "Refreshing..." : "Refresh List"}
                  </button>
                </label>
                
                {loadingTeam ? (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading marketing team...</span>
                  </div>
                ) : marketingTeam.length > 0 ? (
                  <select 
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    className="w-full input border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg px-3 py-2.5 transition-all"
                  >
                    <option value="">Select marketing team member</option>
                    {marketingTeam.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.displayName || `${member.name} (${member.email})`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <FaInfoCircle />
                      <div>
                        <p className="font-medium">No marketing team members found</p>
                        <p className="text-sm">Add workers with role "Marketing" in your company first.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {assignedTo && marketingTeam.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Selected: {marketingTeam.find(m => m.id === assignedTo)?.name}
                  </div>
                )}
              </div>
              
              <div className="pt-4">
                <button
                  disabled={!location || !description || !assignedTo || isSubmitting}
                  onClick={submitLocation}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Assign Location
                    </>
                  )}
                </button>
                
                {(!location || !description || !assignedTo) && (
                  <div className="mt-3 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <div className="font-medium mb-1">Required before submission:</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {!location && <li>Select a location</li>}
                      {!description && <li>Add a description</li>}
                      {!assignedTo && <li>Assign to a team member</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMarketingMap;